import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { X, Loader2, MapPin, UploadCloud, FileVideo, Check, X as XIcon, FileText, Map, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { validateVideoUrl } from '@/lib/utils';
import { appsAdvApiRouterCreateRide, appsAdvApiRouterGetTrackUploadUrl, appsAdvApiRouterProcessTrack, appsAdvApiRouterUploadImage } from '@/lib/api';
import { reverseGeocode, geocodeAddress } from '@/lib/geocoding';
import LocationMap from '@/components/map/LocationMap';
import { TerrainSelector } from '@/components/terrain/TerrainSelector';
import { TerrainType } from '@/lib/types/core/terrain';

interface RideSubmissionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess: () => void;
}

type FormData = {
  title: string;
  ride_type: 'Street' | 'Seeking Off-road' | 'Fully Off-road';
  rider_skill: 'Beginner' | 'Intermediate' | 'Advanced';
  terrain_difficulty: 'Easy' | 'Moderate' | 'Hard';
  meeting_location_lat: string;
  meeting_location_lng: string;
  meeting_address: string;
  meeting_date: string;
  kickstands_up: string;
  description: string;
  gpx_link?: string;
  video_url?: string;
  max_participants?: number;
  requires_approval?: boolean;
  multi_day?: boolean;
  end_date?: string;
  end_date_max?: string;
  dates_flexible?: boolean;
  overnight_plan?: string;
  meal_plan?: string;
  expected_terrain: TerrainType[];
};

interface LocationInputProps {
  onLocationChange: (lat: number, lng: number) => void;
  onGeocodingStatus: (isValid: boolean) => void;
  onAddressChange: (address: string) => void;
  register: any;
  errors: any;
  latitude?: number;
  longitude?: number;
  initialAddress?: string;
}

const LocationInput = ({ onLocationChange, onGeocodingStatus, onAddressChange, register, errors, latitude, longitude, initialAddress }: LocationInputProps) => {
  const [address, setAddress] = useState(initialAddress || '');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'success' | 'error'>(initialAddress ? 'success' : 'idle');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [showMap, setShowMap] = useState(true); // Show map by default in step view
  const { toast } = useToast();

  // Restore valid state when remounting with an existing address
  useEffect(() => {
    if (initialAddress) {
      onGeocodingStatus(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    setGeocodingStatus('idle');
    onGeocodingStatus(false);

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Set new timer
    const timer = setTimeout(() => {
      if (newAddress.trim()) {
        performGeocoding(newAddress);
      }
    }, 1000);

    setDebounceTimer(timer);
  };

  const performGeocoding = async (addressToGeocode: string) => {
    setIsGeocoding(true);
    setGeocodingStatus('idle');

    try {
      const results = await geocodeAddress(addressToGeocode, { limit: 1 });

      if (results.length > 0) {
        const [lng, lat] = results[0].coordinates;
        onLocationChange(lat, lng);
        onAddressChange(addressToGeocode);
        setGeocodingStatus('success');
        onGeocodingStatus(true);
      } else {
        setGeocodingStatus('error');
        onGeocodingStatus(false);
        toast({
          title: "Address not found",
          description: "Could not find coordinates for this address.",
          variant: "destructive"
        });
      }
    } catch (error) {
      setGeocodingStatus('error');
      onGeocodingStatus(false);
      toast({
        title: "Geocoding failed",
        description: "Failed to convert address to coordinates.",
        variant: "destructive"
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocation unavailable",
        description: "Your browser doesn't support geolocation.",
        variant: "destructive"
      });
      return;
    }

    setIsGeocoding(true);
    setGeocodingStatus('idle');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        onLocationChange(latitude, longitude);

        // Reverse geocode to get address
        try {
          const results = await reverseGeocode([longitude, latitude], { limit: 1 });
          if (results.length > 0) {
            const address = results[0].placeName;
            setAddress(address);
            onAddressChange(address);
          }
        } catch (error) {
          console.error("Failed to get address:", error);
        }

        setGeocodingStatus('success');
        onGeocodingStatus(true);
        setIsGeocoding(false);
      },
      (error) => {
        setIsGeocoding(false);
        setGeocodingStatus('error');
        onGeocodingStatus(false);
        toast({
          title: "Location error",
          description: error.message,
          variant: "destructive"
        });
      }
    );
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">
          Meeting Location <span className="text-adv-orange">*</span>
        </label>
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Input
              {...register('meeting_address', { required: "Meeting address is required" })}
              value={address}
              onChange={handleAddressChange}
              placeholder="Enter address (e.g., 123 Main St, Nashville, TN)"
              className="w-full px-4 py-2.5 pr-10 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isGeocoding && (
                <Loader2 size={16} className="animate-spin text-adv-orange" />
              )}
              {geocodingStatus === 'success' && !isGeocoding && (
                <Check size={16} className="text-green-500" />
              )}
              {geocodingStatus === 'error' && !isGeocoding && (
                <XIcon size={16} className="text-red-500" />
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={getCurrentLocation}
              disabled={isGeocoding}
              variant="outline"
              className="flex-1 whitespace-nowrap border-adv-darkGray hover:bg-adv-darkGray"
            >
              {isGeocoding ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Getting Location...
                </>
              ) : (
                <>
                  <MapPin size={16} className="mr-2" />
                  Use Current Location
                </>
              )}
            </Button>
            <Button
              type="button"
              onClick={() => setShowMap(!showMap)}
              variant="outline"
              className="whitespace-nowrap border-adv-darkGray text-adv-lightGray hover:bg-adv-darkGray"
            >
              {showMap ? 'Hide Map' : 'Show Map'}
            </Button>
          </div>
        </div>
        {errors.meeting_address && (
          <p className="mt-1 text-sm text-red-500">{errors.meeting_address.message}</p>
        )}
        <p className="mt-2 text-xs text-adv-lightGray">
          Enter an address, use your current location, or click on the map to place a pin
        </p>
      </div>

      {showMap && (
        <div className="mt-2">
          <LocationMap
            latitude={latitude}
            longitude={longitude}
            onLocationChange={onLocationChange}
            onAddressChange={(newAddress) => {
              setAddress(newAddress);
              onAddressChange(newAddress);
              setGeocodingStatus('success');
              onGeocodingStatus(true);
            }}
            height="280px"
            className="rounded-lg border border-adv-darkGray"
          />
        </div>
      )}
    </div>
  );
};

type GpxUploadStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

// Step configuration
const STEPS = [
  { id: 1, title: 'Basics', description: 'Set the basics for your ride' },
  { id: 2, title: 'Schedule', description: 'Dates and duration' },
  { id: 3, title: 'Type', description: 'Classify your ride' },
  { id: 4, title: 'Details', description: 'Describe what to expect' },
  { id: 5, title: 'Media', description: 'Optional photos and routes' },
];

const RideSubmissionForm = ({ isOpen, onClose, onSubmitSuccess }: RideSubmissionFormProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const currentStepRef = useRef(currentStep);

  // Keep ref in sync with state to avoid stale closures
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLocationValid, setIsLocationValid] = useState(false);
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [gpxFile, setGpxFile] = useState<File | null>(null);
  const [gpxUploadStatus, setGpxUploadStatus] = useState<GpxUploadStatus>('idle');
  const [allowDownload, setAllowDownload] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setValue,
    reset,
    watch,
    trigger
  } = useForm<FormData>({
    defaultValues: {
      ride_type: 'Street',
      rider_skill: 'Beginner',
      terrain_difficulty: 'Easy',
      meeting_date: new Date().toISOString().split('T')[0],
      kickstands_up: '09:00',
      requires_approval: false,
      multi_day: false,
      end_date: '',
      end_date_max: '',
      dates_flexible: false,
      overnight_plan: '',
      meal_plan: '',
      expected_terrain: []
    }
  });

  const videoUrl = watch('video_url');

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  const handleLocationChange = useCallback((lat: number, lng: number) => {
    setValue('meeting_location_lat', lat.toString());
    setValue('meeting_location_lng', lng.toString());
    setLatitude(lat);
    setLongitude(lng);
  }, [setValue]);

  const handleGeocodingStatus = useCallback((isValid: boolean) => {
    setIsLocationValid(isValid);
  }, []);

  const handleAddressChange = useCallback((address: string) => {
    setValue('meeting_address', address);
  }, [setValue]);

  // GPX dropzone handlers
  const onGpxDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setGpxFile(file);
    setGpxUploadStatus('idle');
  }, []);

  const { getRootProps: getGpxRootProps, getInputProps: getGpxInputProps, isDragActive: isGpxDragActive, isDragReject: isGpxDragReject } = useDropzone({
    onDrop: onGpxDrop,
    accept: {
      'application/gpx+xml': ['.gpx'],
      'application/xml': ['.gpx'],
      'text/xml': ['.gpx'],
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
    disabled: gpxUploadStatus === 'uploading' || gpxUploadStatus === 'processing',
  });

  const uploadGpxTrack = async (rideId: string): Promise<boolean> => {
    if (!gpxFile) return true;

    setGpxUploadStatus('uploading');

    try {
      // 1. Get presigned URL
      const uploadUrlRes = await appsAdvApiRouterGetTrackUploadUrl(rideId);
      if (uploadUrlRes.status !== 200 || !uploadUrlRes.data) {
        throw new Error('Failed to get upload URL');
      }
      const { url, fields, s3_key } = uploadUrlRes.data;

      // 2. Upload to S3
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', gpxFile);

      const uploadRes = await fetch(url, { method: 'POST', body: formData });
      if (!uploadRes.ok) throw new Error('S3 upload failed');

      // 3. Trigger processing
      setGpxUploadStatus('processing');
      const processRes = await appsAdvApiRouterProcessTrack(rideId, {
        s3_key,
        original_filename: gpxFile.name,
        file_size: gpxFile.size,
        allow_download: allowDownload,
      });

      if (processRes.status !== 200) {
        throw new Error('Processing failed to start');
      }

      setGpxUploadStatus('completed');
      setGpxFile(null);
      return true;
    } catch (error) {
      setGpxUploadStatus('failed');
      console.error('GPX upload error:', error);
      toast({
        title: "GPX upload failed",
        description: "The ride was created, but the GPX track could not be uploaded. You can add it later.",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);

      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validation for each step
  const validateStep = async (step: number): Promise<boolean> => {
    switch (step) {
      case 1: // Basics: Title, Location
        const titleValid = await trigger(['title']);
        if (!titleValid) return false;
        if (!isLocationValid || !watch('meeting_address')?.trim()) {
          toast({
            title: "Location required",
            description: "Please enter a valid meeting location.",
            variant: "destructive"
          });
          return false;
        }
        return true;
      case 2: // Schedule: Date, Time, Duration
        return await trigger(['meeting_date', 'kickstands_up']);
      case 3: // Type: Classification fields
        return await trigger(['ride_type', 'rider_skill', 'terrain_difficulty']);
      case 4: // Details: Description
        return await trigger(['description']);
      case 5: // Media: All optional
        return true;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid && currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = async (step: number) => {
    // Only allow going backwards or to current step
    if (step < currentStep) {
      setCurrentStep(step);
    }
  };

  const validateVideo = () => {
    if (!videoUrl) return true;

    const result = validateVideoUrl(videoUrl);
    if (!result.valid) {
      toast({
        title: "Invalid video URL",
        description: "Please enter a valid YouTube, Instagram, or TikTok URL.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const onSubmit = async (data: FormData) => {
    // Only allow submission from the final step (use ref to avoid stale closure)
    if (currentStepRef.current !== STEPS.length) {
      return;
    }

    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to submit a ride.",
        variant: "destructive"
      });
      return;
    }

    if (!validateVideo()) return;

    if (!isLocationValid) {
      toast({
        title: "Invalid location",
        description: "Please enter a valid address or use your current location.",
        variant: "destructive"
      });
      setCurrentStep(1);
      return;
    }

    if (!data.meeting_address?.trim()) {
      toast({
        title: "Meeting address required",
        description: "Please enter a meeting address.",
        variant: "destructive"
      });
      setCurrentStep(1);
      return;
    }

    let imageUrl = null;

    try {
      // Upload image if selected
      if (selectedImage) {
        setUploading(true);

        const uploadResponse = await appsAdvApiRouterUploadImage({ image: selectedImage });
        imageUrl = uploadResponse.data.image_url;
        setUploading(false);
      }

      // Create ride using our API
      const rideResponse = await appsAdvApiRouterCreateRide({
        title: data.title,
        ride_type: data.ride_type,
        rider_skill: data.rider_skill,
        terrain_difficulty: data.terrain_difficulty,
        meeting_latitude: parseFloat(data.meeting_location_lat),
        meeting_longitude: parseFloat(data.meeting_location_lng),
        meeting_address: data.meeting_address,
        meeting_date: data.meeting_date,
        kickstands_up: data.kickstands_up,
        description: data.description,
        gpx_link: data.gpx_link || undefined,
        image_url: imageUrl || undefined,
        video_url: data.video_url || undefined,
        max_participants: data.max_participants || undefined,
        requires_approval: data.requires_approval,
        multi_day: data.multi_day,
        end_date: data.end_date || undefined,
        end_date_max: data.end_date_max || undefined,
        dates_flexible: data.dates_flexible,
        overnight_plan: data.overnight_plan || undefined,
        meal_plan: data.meal_plan || undefined,
        expected_terrain: data.expected_terrain
      });

      // Upload GPX track if selected (after ride is created)
      if (gpxFile && rideResponse.data?.id) {
        await uploadGpxTrack(rideResponse.data.id);
      }

      toast({
        title: "Ride submitted successfully!",
        description: gpxFile ? "Your ride and GPX track have been submitted." : "Your ride has been added to the map."
      });

      reset();
      setSelectedImage(null);
      setPreviewUrl(null);
      setIsLocationValid(false);
      setLatitude(undefined);
      setLongitude(undefined);
      setGpxFile(null);
      setGpxUploadStatus('idle');
      setAllowDownload(true);
      setCurrentStep(1);
      onSubmitSuccess();
      onClose();

    } catch (error: any) {
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Step 1: Basics (Title, Location, Participants)
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">
          Ride Title <span className="text-adv-orange">*</span>
        </label>
        <Input
          {...register('title', { required: "Title is required" })}
          placeholder="e.g., Mountain Pass Adventure"
          className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      <LocationInput
        onLocationChange={handleLocationChange}
        onGeocodingStatus={handleGeocodingStatus}
        onAddressChange={handleAddressChange}
        register={register}
        errors={errors}
        latitude={latitude}
        longitude={longitude}
        initialAddress={watch('meeting_address')}
      />

      <div>
        <label className="block text-sm font-medium mb-2">
          Max Participants
        </label>
        <Input
          type="number"
          min="1"
          max="100"
          {...register('max_participants', {
            min: { value: 1, message: "Must be at least 1" },
            max: { value: 100, message: "Cannot exceed 100" }
          })}
          placeholder="Leave blank for unlimited"
          className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
        />
        {errors.max_participants && (
          <p className="mt-1 text-sm text-red-500">{errors.max_participants.message}</p>
        )}
        <p className="mt-1 text-xs text-adv-lightGray">
          Set a limit or leave blank for unlimited spots
        </p>
      </div>

      <div className="flex items-start space-x-3">
        <Controller
          name="requires_approval"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="requires_approval"
              checked={field.value}
              onCheckedChange={field.onChange}
              className="mt-0.5"
            />
          )}
        />
        <div>
          <label htmlFor="requires_approval" className="text-sm font-medium cursor-pointer block">
            Require approval for RSVPs
          </label>
          <p className="text-xs text-adv-lightGray mt-0.5">
            You'll review and approve each rider before they join
          </p>
        </div>
      </div>
    </div>
  );

  // Step 2: Schedule (Dates and duration)
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Meeting Date <span className="text-adv-orange">*</span>
          </label>
          <Input
            type="date"
            {...register('meeting_date', { required: "Meeting date is required" })}
            className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
          />
          {errors.meeting_date && (
            <p className="mt-1 text-sm text-red-500">{errors.meeting_date.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Kickstands Up <span className="text-adv-orange">*</span>
          </label>
          <Input
            type="time"
            {...register('kickstands_up', { required: "Kickstands up time is required" })}
            className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
          />
          {errors.kickstands_up && (
            <p className="mt-1 text-sm text-red-500">{errors.kickstands_up.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-4 pt-2">
        <div>
          <label className="text-sm font-medium block mb-2">Duration</label>
          <Controller
            name="multi_day"
            control={control}
            render={({ field }) => (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    field.onChange(false);
                    setValue('end_date', '');
                    setValue('end_date_max', '');
                    setValue('dates_flexible', false);
                    setValue('overnight_plan', '');
                    setValue('meal_plan', '');
                  }}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                    !field.value
                      ? 'bg-adv-orange text-white border-adv-orange'
                      : 'bg-white text-adv-darkGray border-adv-darkGray hover:border-adv-orange'
                  }`}
                >
                  Single Day
                </button>
                <button
                  type="button"
                  onClick={() => field.onChange(true)}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border transition-all ${
                    field.value
                      ? 'bg-adv-orange text-white border-adv-orange'
                      : 'bg-white text-adv-darkGray border-adv-darkGray hover:border-adv-orange'
                  }`}
                >
                  Multi-Day
                </button>
              </div>
            )}
          />
        </div>

        {watch('multi_day') && (
          <div className="space-y-4 pl-1 border-l-2 border-adv-orange/30 ml-1 animate-fade-in">
            <div className="pl-3">
              <label className="text-sm font-medium block mb-1">End Date *</label>
              <Input
                type="date"
                {...register('end_date', {
                  required: watch('multi_day') ? 'End date is required for multi-day rides' : false,
                  validate: (value) => {
                    if (!value) return true;
                    const meetingDate = watch('meeting_date');
                    if (meetingDate && value <= meetingDate) {
                      return 'End date must be after the start date';
                    }
                    return true;
                  }
                })}
                className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
              />
              {errors.end_date && (
                <p className="mt-1 text-sm text-red-500">{errors.end_date.message}</p>
              )}
            </div>

            <div className="pl-3">
              <div className="flex items-start space-x-3">
                <Controller
                  name="dates_flexible"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="dates_flexible"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (!checked) setValue('end_date_max', '');
                      }}
                      className="mt-0.5"
                    />
                  )}
                />
                <label htmlFor="dates_flexible" className="text-sm font-medium cursor-pointer block">
                  Dates are flexible
                </label>
              </div>

              {watch('dates_flexible') && (
                <div className="mt-2">
                  <label className="text-sm font-medium block mb-1">End Date (Latest)</label>
                  <Input
                    type="date"
                    {...register('end_date_max', {
                      validate: (value) => {
                        if (!value) return true;
                        const endDate = watch('end_date');
                        if (endDate && value <= endDate) {
                          return 'Latest end date must be after the end date';
                        }
                        return true;
                      }
                    })}
                    className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
                  />
                  {errors.end_date_max && (
                    <p className="mt-1 text-sm text-red-500">{errors.end_date_max.message}</p>
                  )}
                </div>
              )}
            </div>

            <div className="pl-3">
              <label className="text-sm font-medium block mb-1">Overnight Plan</label>
              <select
                {...register('overnight_plan')}
                className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all bg-white"
              >
                <option value="">Select...</option>
                <option value="camping">Camping</option>
                <option value="hotel">Hotel</option>
                <option value="mixed">Mixed</option>
              </select>
              <p className="mt-1 text-xs text-adv-lightGray">Where will riders stay overnight? Organizer should include details in description</p>
            </div>

            <div className="pl-3">
              <label className="text-sm font-medium block mb-1">Meal Plan</label>
              <select
                {...register('meal_plan')}
                className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all bg-white"
              >
                <option value="">Select...</option>
                <option value="bring_your_own">Bring Your Own</option>
                <option value="restaurants">Restaurants</option>
                <option value="mixed">Mixed</option>
              </select>
              <p className="mt-1 text-xs text-adv-lightGray">How will meals be handled? Organizer should include details in description</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );

  // Step 3: Type (Classification)
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Ride Type <span className="text-adv-orange">*</span>
          </label>
          <select
            {...register('ride_type', { required: "Ride type is required" })}
            className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all bg-transparent"
          >
            <option value="Street">Street</option>
            <option value="Seeking Off-road">Seeking Off-road</option>
            <option value="Fully Off-road">Fully Off-road</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Rider Skill Level <span className="text-adv-orange">*</span>
          </label>
          <select
            {...register('rider_skill', { required: "Skill level is required" })}
            className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all bg-transparent"
          >
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Terrain Difficulty <span className="text-adv-orange">*</span>
          </label>
          <select
            {...register('terrain_difficulty', { required: "Difficulty is required" })}
            className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all bg-transparent"
          >
            <option value="Easy">Easy</option>
            <option value="Moderate">Moderate</option>
            <option value="Hard">Hard</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium">
          Expected Terrain
        </label>
        <Controller
          name="expected_terrain"
          control={control}
          render={({ field }) => (
            <TerrainSelector
              value={field.value}
              onChange={field.onChange}
              disabled={isSubmitting}
            />
          )}
        />
        <p className="text-xs text-adv-lightGray">
          Select all terrain types riders should expect
        </p>
      </div>
    </div>
  );

  // Step 4: Details (Description)
  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">
          Description <span className="text-adv-orange">*</span>
        </label>
        <p className="text-sm text-adv-lightGray mb-3">
          Give this ride a description that makes people want to show up!
        </p>
        <Textarea
          {...register('description', {
            required: "Description is required",
            minLength: { value: 20, message: "Description must be at least 20 characters" }
          })}
          rows={8}
          placeholder="Describe the ride, terrain, highlights, what to expect, any stops planned..."
          className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
        )}
        <p className="mt-2 text-xs text-adv-lightGray">
          Minimum 20 characters. Include details about stops, points of interest, or what makes this ride special.
        </p>
      </div>
    </div>
  );

  // Step 5: Media & Routes
  const renderStep5 = () => {
    const isDisabled = gpxUploadStatus === 'uploading' || gpxUploadStatus === 'processing';

    return (
      <div className="space-y-6">
        <p className="text-sm text-adv-lightGray">
          All fields on this step are optional. Add media to make your ride more appealing.
        </p>

        {/* GPX Track Upload */}
        <div>
          <label className="block text-sm font-medium mb-2">
            GPX Track
          </label>
          <div
            {...getGpxRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
              ${isGpxDragActive ? 'border-adv-orange bg-adv-orange/10' : 'border-adv-darkGray'}
              ${isGpxDragReject ? 'border-red-500 bg-red-500/10' : ''}
              ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-adv-charcoal'}
            `}
          >
            <input {...getGpxInputProps()} />
            {gpxFile ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="h-5 w-5 text-green-500" />
                <span className="text-sm">{gpxFile.name}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setGpxFile(null); setGpxUploadStatus('idle'); }}
                  className="text-red-500 hover:text-red-400 ml-2"
                  disabled={isDisabled}
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="py-2">
                <Map className="h-6 w-6 mx-auto text-adv-lightGray mb-2" />
                <p className="text-sm text-adv-lightGray">Drop GPX file or click to select</p>
              </div>
            )}
          </div>
          {gpxFile && (
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="allow_download"
                checked={allowDownload}
                onCheckedChange={(checked) => setAllowDownload(checked === true)}
              />
              <label htmlFor="allow_download" className="text-sm text-adv-lightGray">
                Allow RSVP'd riders to download GPX
              </label>
            </div>
          )}
        </div>

        {/* Route Link */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Route Link
          </label>
          <Input
            {...register('gpx_link')}
            placeholder="GaiaGPS, Google Maps, Rever, etc."
            className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
          />
        </div>

        {/* Ride Image */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Ride Image
          </label>
          <input
            ref={imageInputRef}
            type="file"
            className="sr-only"
            accept="image/*"
            onChange={handleImageChange}
          />
          <div
            role="button"
            tabIndex={0}
            onClick={() => imageInputRef.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); imageInputRef.current?.click(); } }}
            className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-adv-darkGray rounded-lg cursor-pointer hover:bg-adv-charcoal transition-colors"
          >
            {previewUrl ? (
              <div className="flex items-center gap-3">
                <img src={previewUrl} alt="Preview" className="h-16 w-16 object-cover rounded" />
                <span className="text-sm text-adv-lightGray">Click to change</span>
              </div>
            ) : (
              <div className="flex flex-col items-center py-2">
                <UploadCloud size={20} className="text-adv-orange mb-1" />
                <p className="text-sm text-adv-lightGray">Click to upload image</p>
              </div>
            )}
          </div>
        </div>

        {/* Video Link */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Video Link
          </label>
          <div className="flex items-center">
            <FileVideo size={18} className="text-adv-orange mr-2 flex-shrink-0" />
            <Input
              {...register('video_url')}
              placeholder="YouTube, Instagram, or TikTok URL"
              className="w-full px-4 py-3 border border-adv-darkGray rounded-md focus:ring-2 focus:ring-adv-orange focus:border-transparent outline-none transition-all"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-6">
      {/* Progress bar */}
      <div className="relative mb-4">
        <div className="h-1 bg-adv-darkGray rounded-full">
          <div
            className="h-1 bg-adv-orange rounded-full transition-all duration-300"
            style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Step dots */}
      <div className="grid grid-cols-5">
        {STEPS.map((step) => (
          <button
            key={step.id}
            type="button"
            onClick={() => goToStep(step.id)}
            className={`flex flex-col items-center group ${step.id <= currentStep ? 'cursor-pointer' : 'cursor-default'}`}
            disabled={step.id > currentStep}
          >
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
              transition-all duration-200
              ${step.id === currentStep
                ? 'bg-adv-orange text-white scale-110'
                : step.id < currentStep
                  ? 'bg-adv-orange/80 text-white'
                  : 'bg-adv-darkGray text-adv-lightGray'}
            `}>
              {step.id < currentStep ? <Check size={14} /> : step.id}
            </div>
            <span className={`
              text-xs mt-1
              ${step.id === currentStep
                ? 'block text-adv-orange font-medium'
                : 'hidden sm:block text-adv-lightGray'}
            `}>
              {step.title}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderNavigation = () => {
    const isGpxBusy = gpxUploadStatus === 'uploading' || gpxUploadStatus === 'processing';
    const isBusy = isSubmitting || uploading || isGpxBusy;
    const isLastStep = currentStep === STEPS.length;

    return (
      <div className="flex justify-between items-center pt-6 mt-6 border-t border-adv-darkGray">
        <div>
          {currentStep > 1 && (
            <Button
              type="button"
              onClick={handleBack}
              variant="outline"
              className="border-adv-darkGray text-adv-lightGray hover:bg-adv-darkGray"
              disabled={isBusy}
            >
              <ChevronLeft size={16} className="mr-1" />
              Back
            </Button>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={onClose}
            variant="ghost"
            className="text-adv-lightGray hover:text-white"
            disabled={isBusy}
          >
            Cancel
          </Button>

          {isLastStep ? (
            <Button
              type="button"
              onClick={() => handleSubmit(onSubmit)()}
              className="btn-primary"
              disabled={isBusy}
            >
              {isBusy ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  {isGpxBusy ? 'Uploading...' : 'Submitting...'}
                </>
              ) : 'Submit Ride'}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              className="btn-primary"
            >
              Next
              <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card rounded-lg p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold">Submit a Ride</h3>
            <p className="text-sm text-adv-lightGray mt-1">
              {STEPS[currentStep - 1].description}
            </p>
          </div>
          <button onClick={onClose} className="text-adv-lightGray hover:text-white">
            <X size={20} />
          </button>
        </div>

        {renderStepIndicator()}

        <div
          onKeyDown={(e) => {
            // Prevent Enter key from submitting/navigating, but allow it in textareas
            if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement)) {
              e.preventDefault();
              if (currentStepRef.current === STEPS.length) {
                handleSubmit(onSubmit)();
              }
            }
          }}
        >
          <div className="min-h-[300px]">
            {renderCurrentStep()}
          </div>
          {renderNavigation()}
        </div>
      </div>
    </div>
  );
};

export default RideSubmissionForm;
